"use client";

import { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axios";
import { Button, HelpAttachment } from "djinn-components";
import { toast } from "react-toastify";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { SiteType } from "../../types/api";
import LoadingComponent from "../LoadingComponent";
import { ProvideSite } from "../../utils/useSiteState";
import Sidebar from "../Sidebars";

const SiteEditLayout = ({ children }: { children?: React.ReactNode }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [site, setSite] = useState<SiteType | null>(null);

  useEffect(() => {
    if (!site || String(site.id) !== params?.siteId) {
      if (params?.siteId) getSite();
    }
  }, [params?.siteId]); // eslint-disable-line

  const getSite = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`sites/${params?.siteId}`);
      setSite(data as SiteType);
    } catch (error) {
      toast(
        "Failed to fetch site, if this is an error please contact support",
        {
          type: "error",
        }
      );
      navigate("/");
    }
    setLoading(false);
  };

  const reloadSite = async () => {
    try {
      const { data } = await axios.get(`sites/${params?.siteId}`);
      await setSite(data as SiteType);
      return;
    } catch (error) {
      toast(
        "Failed to fetch site, if this is an error please contact support",
        { type: "error" }
      );

      navigate("/");
    }
  };

  const expired = useMemo(
    () =>
      site?.active_subscription === "none" &&
      site.trial_expires_at < new Date().toISOString(),
    [site?.active_subscription, site?.trial_expires_at]
  );

  if (!site || loading) return <LoadingComponent />;
  if (!loading && !site) return <div>Failed to load site</div>;

  return (
    <ProvideSite site={site} reload={() => reloadSite()}>
      <div className="flex items-start justify-start bg-gray-800">
        <Sidebar />
        <div className={`h-screen overflow-hidden w-screen`}>
          {expired ? (
            <div className="mt-10 max-w-5xl mx-auto text-white bg-gray-500 rounded-lg p-6 bg-opacity-20 border-white border-1 flex items-center justify-between">
              <div>
                {!site.stripe_id ? (
                  <>
                    <h2 className="text-xl font-semibold">Trial has ended</h2>
                    <p className="max-w-xl text-gray-300">
                      Your 30 day trial of appsicle has ended, please choose a
                      plan to continue building your site.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-semibold">
                      Subscription failed to renew
                    </h2>
                    <p className="max-w-xl text-gray-300">
                      Your last payment failed to complete and your site has
                      been suspended until you review your payment method.
                    </p>
                  </>
                )}
              </div>

              <div className="w-[180px]">
                <Button
                  text={site.stripe_id === null ? "Choose Plan" : "Renew"}
                  onClick={() =>
                    navigate(`/account/subscriptions?choose-plan=${site.id}`)
                  }
                ></Button>
              </div>
            </div>
          ) : (
            <>
              <Outlet />
            </>
          )}
        </div>
        <HelpAttachment
          appPath="appsicle"
          appName="Appsicle"
          appToken={process.env.REACT_APP_DJINN_ADMIN_API_KEY as string}
          logoPath="/icon.png"
        />
      </div>
    </ProvideSite>
  );
};

export default SiteEditLayout;
